import { Box, Grid, Typography } from '@mui/material';
import React, { ReactNode } from 'react';

/**
 * One labelled field on the referral card: an input when editing, a read-only value
 * otherwise.
 *
 * Moved out of `ReferralDocumentsTab` so `CoverageDetail` renders through the same cell
 * instead of re-implementing it — two copies of "label, value, dash when empty" are two
 * places for the styling to drift apart.
 */
export interface FieldWrapperProps {
  label: string;
  isEditing: boolean;
  readValue: string | null | undefined;
  /** A note under the read-only value, such as how it was obtained. Not shown when editing. */
  readHint?: ReactNode;
  children?: ReactNode;
  gridSize?: { xs: number; md: number };
  required?: boolean;
}

const FieldWrapper: React.FC<FieldWrapperProps> = ({
  label,
  isEditing,
  readValue,
  readHint,
  children,
  gridSize = { xs: 12, md: 4 },
  required = false,
}) => {
  const hasValue = readValue !== null && readValue !== '' && readValue !== undefined;

  return (
    <Grid item {...gridSize}>
      <Typography
        variant='caption'
        color='text.secondary'
        display='block'
        sx={{
          mb: 0.5,
          fontWeight: isEditing ? 600 : 400,
        }}>
        {label} {isEditing && required && <span style={{ color: '#d32f2f' }}>*</span>}
      </Typography>
      {isEditing ? (
        <Box
          sx={{
            '& .MuiTextField-root': {
              bgcolor: 'background.paper',
              borderRadius: 1,
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
              },
            },
          }}>
          {children}
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            minHeight: 32,
            p: 1,
            bgcolor: 'grey.50',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.200',
          }}>
          <Typography variant='body2' sx={{ color: hasValue ? 'text.primary' : 'text.disabled' }}>
            {hasValue ? String(readValue) : '—'}
          </Typography>
        </Box>
      )}
      {!isEditing && readHint}
    </Grid>
  );
};

export default FieldWrapper;
